import type { InspectionStage } from './inspection'
import type { DeliveryLeg, Permission } from './vocabulary'

/**
 * S9 (Q-62): what a person may do on one booking. The booking's answer says
 * it to the app, which decides nothing. A role's permission allows an action
 * on any booking. A trip given to someone allows, on its own booking only,
 * the actions of its leg: the delivery is the hand-over, the collection is
 * the return. A payment fits either leg. The rest stays with the office,
 * whoever drives: closing the booking, typing a CaRGOS receipt, the card
 * reader, keeping a deposit, fiscal documents, bank transfers, refunds,
 * cancelling, moving the booking, overrides.
 */
export const BOOKING_ACTIONS = [
  /** The booking and its inspection. */
  'read',
  /**
   * Readiness and the hand-over: the identity, sending it to CaRGOS, the
   * places it names, document photos, the vehicle's photos and damages at
   * the hand-over, and turning the device to the customer.
   */
  'handOver',
  /** Document photos, seen. */
  'viewDocuments',
  /** A deposit taken by hand: on a portable terminal, or in cash. */
  'depositTake',
  /** The return, with the vehicle's photos and damages at it. */
  'return',
  /** The whole deposit let go. */
  'depositRelease',
  /** Cash or a card on a terminal, a payment link, and the drawers open to put cash in. */
  'payment',
  /** A deposit on the card reader at the counter: taking it, checking it, its test card. */
  'cardReader',
  'depositKeep',
  /** A receipt or invoice, with a payment or alone. */
  'fiscalDocument',
  'bankTransfer',
  /** A CaRGOS receipt typed by hand. */
  'cargosReceipt',
  'close',
  'refund',
  /** Cancelling, or a no-show. */
  'cancel',
  'reschedule',
  /** Letting the vehicle go with a check failing. */
  'override',
  'contractPdf',
  /** The customer's own page. */
  'openCustomer',
  /** Giving the booking's trips to someone. */
  'trips',
  /** S11: a rental kept on paper while the server was down, typed back in (the Owner's). */
  'reenter',
  /** Q-162: a second driver added, changed or taken off before the hand-over (the office's). */
  'secondDriver',
] as const
export type BookingAction = (typeof BOOKING_ACTIONS)[number]

const EITHER: readonly DeliveryLeg[] = ['delivery', 'collection']
const DELIVERY: readonly DeliveryLeg[] = ['delivery']
const COLLECTION: readonly DeliveryLeg[] = ['collection']
const OFFICE: readonly DeliveryLeg[] = []

export interface BookingActionRule {
  /** The permission that allows the action on any booking. */
  permission: Permission
  /** The legs whose trip allows it on the trip's own booking; none: the office only. */
  legs: readonly DeliveryLeg[]
}

export const BOOKING_ACTION_RULES: Readonly<Record<BookingAction, BookingActionRule>> = {
  read: { permission: 'bookings.read', legs: EITHER },
  handOver: { permission: 'rentals.manage', legs: DELIVERY },
  viewDocuments: { permission: 'customers.read', legs: DELIVERY },
  depositTake: { permission: 'rentals.manage', legs: DELIVERY },
  return: { permission: 'rentals.manage', legs: COLLECTION },
  depositRelease: { permission: 'rentals.manage', legs: COLLECTION },
  payment: { permission: 'payments.record', legs: EITHER },
  cardReader: { permission: 'rentals.manage', legs: OFFICE },
  depositKeep: { permission: 'payments.record', legs: OFFICE },
  fiscalDocument: { permission: 'payments.record', legs: OFFICE },
  bankTransfer: { permission: 'payments.record', legs: OFFICE },
  cargosReceipt: { permission: 'rentals.manage', legs: OFFICE },
  close: { permission: 'rentals.manage', legs: OFFICE },
  refund: { permission: 'payments.refund', legs: OFFICE },
  cancel: { permission: 'bookings.cancel', legs: OFFICE },
  reschedule: { permission: 'bookings.reschedule', legs: OFFICE },
  override: { permission: 'checkout.override', legs: OFFICE },
  contractPdf: { permission: 'bookings.read', legs: OFFICE },
  openCustomer: { permission: 'customers.read', legs: OFFICE },
  trips: { permission: 'deliveries.manage', legs: OFFICE },
  reenter: { permission: 'rentals.reenter', legs: OFFICE },
  secondDriver: { permission: 'rentals.manage', legs: OFFICE },
}

/**
 * One action: by the role, or by a trip of this booking given to the
 * person, whose leg the action belongs to. `legs` are those of the person's
 * trips on this booking that are live now (tripGivesScope).
 */
export function bookingActionAllowed(
  permissions: readonly Permission[],
  legs: readonly DeliveryLeg[],
  action: BookingAction,
): boolean {
  const rule = BOOKING_ACTION_RULES[action]
  return permissions.includes(rule.permission) || rule.legs.some((leg) => legs.includes(leg))
}

/** Every action the person may take on the booking, in the list's order. */
export function bookingActions(
  permissions: readonly Permission[],
  legs: readonly DeliveryLeg[],
): BookingAction[] {
  return BOOKING_ACTIONS.filter((action) => bookingActionAllowed(permissions, legs, action))
}

/** The action a vehicle photo or damage belongs to: the hand-over's, or the return's. */
export function inspectionAction(stage: InspectionStage): BookingAction {
  return stage === 'checkout' ? 'handOver' : 'return'
}
